










































































































































































import { Vue, Component } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { sortBy } from 'lodash';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import ConversationService from '@/services/crm/conversation.service';
import ConversationModel from '@/models/crm/conversation.model';
import ConversationTemplateModel from '@/models/crm/conversation-template.model';
import UserModel from '@/models/user.model';
import ConversationUserPermissionsModel from '@/models/crm/conversation-user-permissions.model';
import ConversationDepartmentModel from '@/models/crm/conversation-department.model';
import SettingsService from '@/services/crm/settings.service';
import SettingsModel from '@/models/crm/settings.model';
import { UserTypeEnum } from '@/enums/crm/user-type.enum';
import GeoChatListItem from '@/components/crm/geochat-list-item.vue';
import UserContactInfo from '@/models/crm/user-contact-info.model';
import ContactService from '@/services/crm/contact.service';

const cacheTtlMs = 3 * 60 * 1000; // 3 minutos

interface IActiveGroup {
  attendant: UserModel;
  quantityChats: number;
  items: IGroupItem[];
  loaded: boolean;
  loading: boolean;
  loadedAt: number;
}

interface IQueueGroup {
  department: ConversationDepartmentModel;
  quantityChats: number;
  items: IGroupItem[];
  loaded: boolean;
  loading: boolean;
  loadedAt: number;
}

interface IGroupItem {
  id?: number;
  conversation: ConversationModel;
  unreadCounter?: number;
  loading?: boolean;
}

@Component({
  components: {
    GeoChatListItem,
  },
})
export default class GeoChatOverview extends Vue {
  @inject(InjectionIdEnum.CrmSettingsService)
  private settingsService!: SettingsService;

  @inject(InjectionIdEnum.CrmConversationService)
  protected conversationService!: ConversationService;

  @inject(InjectionIdEnum.CrmContactService)
  private contactService!: ContactService;

  private settings!: SettingsModel;

  conversationUserPermission!: ConversationUserPermissionsModel;

  userContactInfo!: UserContactInfo;

  isManagerDirector = false;

  tab: string | null = null;

  groupedOpenChatList: IActiveGroup[] = [];

  groupedQueueList: IQueueGroup[] = [];

  templates: ConversationTemplateModel[] = [];

  attendants: UserModel[] = [];

  refreshing = false;

  filterOpenChatAttendants = false;

  private totalOpen = 0;

  private totalOnHold = 0;

  async mounted(): Promise<void> {
    const loader = this.$loading.show();
    try {
      this.settings = await this.settingsService.getSettings();
      this.userContactInfo = await this.contactService.getLoggedUserContactInfo();

      this.isManagerDirector = Boolean(this.userContactInfo?.usuarioAdminCRM)
        || Boolean(this.userContactInfo?.usuarioGestorCRM)
        || Boolean(this.userContactInfo?.usuarioDiretorCRM);

      await Promise.all([this.loadTemplates(), this.loadAttendants(), this.loadUserPermissions()]);

      if (!this.conversationUserPermission.masterCrmUser && !this.isManagerDirector) {
        this.$notify.error(this.$t('global.youDoNotHavePermissionToAccessTheScreen'));

        this.$router.back();

        return;
      }

      await this.loadOverviewCounts();
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      loader.hide();
    }
  }

  beforeDestroy(): void {
    this.groupedOpenChatList = [];
    this.groupedQueueList = [];
    this.attendants = [];
    this.templates = [];
  }

  onChatItemClick(item: IGroupItem): void {
    this.$router.push({
      name: 'GeoChatView',
      query: {
        ...this.$route.query,
        conversationId: item.conversation.id.toString(),
      },
    });
  }

  onClickPrevious(): void {
    this.$router.back();
  }

  async loadOverviewCounts(): Promise<void> {
    const counts = await this.conversationService.getOverviewCounts();

    const openByAttendant = counts.openByAttendant || [];
    const onHoldByDepartment = counts.onHoldByDepartment || [];

    const groupedOpenChatList: IActiveGroup[] = [];

    sortBy(openByAttendant, 'nome').forEach((countItem) => {
      const attendant = new UserModel();
      attendant.id = countItem.id;
      attendant.nome = countItem.nome;
      attendant.isRootUser = countItem.isRoot || false;

      const isNotRoot = !this.settings.flagMostraChatsUsuariosRootVisaoGeral && !attendant.isRootUser;
      if (this.settings.flagMostraChatsUsuariosRootVisaoGeral || isNotRoot) {
        groupedOpenChatList.push({
          attendant,
          quantityChats: countItem.count,
          items: [],
          loaded: false,
          loading: false,
          loadedAt: 0,
        });
      }
    });

    sortBy(this.attendants, 'nome').forEach((attendant) => {
      if (groupedOpenChatList.find((x) => x.attendant.id === attendant.id)) {
        return;
      }

      const isNotRoot = !this.settings.flagMostraChatsUsuariosRootVisaoGeral && !attendant.isRootUser;
      if (this.settings.flagMostraChatsUsuariosRootVisaoGeral || isNotRoot) {
        groupedOpenChatList.push({
          attendant,
          quantityChats: 0,
          items: [],
          loaded: false,
          loading: false,
          loadedAt: 0,
        });
      }
    });

    this.groupedOpenChatList = [...groupedOpenChatList];

    const groupedQueueList: IQueueGroup[] = [];

    sortBy(onHoldByDepartment, 'nome').forEach((countItem) => {
      const department = new ConversationDepartmentModel();
      department.id = countItem.id;
      department.nome = countItem.nome;

      groupedQueueList.push({
        department,
        quantityChats: countItem.count,
        items: [],
        loaded: false,
        loading: false,
        loadedAt: 0,
      });
    });

    this.groupedQueueList = [...groupedQueueList];

    this.totalOpen = openByAttendant.reduce((sum, item) => sum + item.count, 0);
    this.totalOnHold = onHoldByDepartment.reduce((sum, item) => sum + item.count, 0);
  }

  async onExpandActiveGroup(activeGroup: IActiveGroup): Promise<void> {
    const target = activeGroup;
    const cacheExpired = target.loaded && (Date.now() - target.loadedAt) > cacheTtlMs;

    if (target.loading || (target.loaded && !cacheExpired)) {
      return;
    }

    target.loading = true;
    try {
      const conversations = await this.conversationService.getOpenConversations(target.attendant.id);

      const items: IGroupItem[] = conversations.map((x) => {
        const conversation = x;
        conversation.messages = [];
        return { conversation };
      });

      items.sort((a, b) => {
        const dateA = a.conversation.dataUltimaMensagem
          ? new Date(a.conversation.dataUltimaMensagem)
          : new Date(a.conversation.dataInicio);
        const dateB = b.conversation.dataUltimaMensagem
          ? new Date(b.conversation.dataUltimaMensagem)
          : new Date(b.conversation.dataInicio);
        return +dateB - +dateA;
      });

      target.items = items;
      target.quantityChats = items.length;
      target.loaded = true;
      target.loadedAt = Date.now();
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      target.loading = false;
    }
  }

  async onExpandQueueGroup(queueGroup: IQueueGroup): Promise<void> {
    const target = queueGroup;
    const cacheExpired = target.loaded && (Date.now() - target.loadedAt) > cacheTtlMs;

    if (target.loading || (target.loaded && !cacheExpired)) {
      return;
    }

    target.loading = true;
    try {
      const conversations = await this.conversationService.getOnHoldConversations(undefined, target.department.id);

      target.items = conversations.map((x) => {
        const conversation = x;
        conversation.messages = [];
        return { conversation };
      });
      target.quantityChats = target.items.length;
      target.loaded = true;
      target.loadedAt = Date.now();
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      target.loading = false;
    }
  }

  async onRefresh(): Promise<void> {
    this.refreshing = true;
    try {
      await this.loadOverviewCounts();
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.refreshing = false;
    }
  }

  get filteredOpenChatList(): IActiveGroup[] {
    if (this.filterOpenChatAttendants) {
      return this.groupedOpenChatList.filter((g) => g.quantityChats > 0);
    }
    return this.groupedOpenChatList;
  }

  get filteredQueueList(): IQueueGroup[] {
    if (this.filterOpenChatAttendants) {
      return this.groupedQueueList.filter((g) => g.quantityChats > 0);
    }
    return this.groupedQueueList;
  }

  get inAttendanceCount(): number {
    return this.totalOpen;
  }

  private getTitleText(): string {
    if (!this.settings || this.settings.loggedUserType === null) {
      return '';
    }

    return this.settings.loggedUserType !== UserTypeEnum.Manager
      ? 'Visão Geral' : 'Visão dos atendentes do gestor';
  }

  get onHoldCount(): number {
    return this.totalOnHold;
  }

  private async loadTemplates(): Promise<void> {
    this.templates = await this.conversationService.getTemplates(false);
  }

  private async loadAttendants(): Promise<void> {
    if (this.isManagerDirector) {
      this.attendants = await this.conversationService.getAttendants(true);
    } else {
      this.attendants = await this.conversationService.getAttendants();
    }
  }

  private async loadUserPermissions(): Promise<void> {
    this.conversationUserPermission = await this.conversationService.getUserPermissions();
  }
}
